import React from "react";
import {
  AbsoluteFill,
  interpolate,
  Sequence,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { C, FONT, INFO, rp } from "./brand";
import { useOnestFonts } from "./fonts";
import { Counter, Pole } from "./parts";

/* ══════════════════════════════════════════════════════════════════
   Pangkasin — video promosi NEO-BRUTALISM (tema sama dengan PangkasinPromo)
   1920x1080 @30fps, 6 <Sequence> × 300 frame (10 detik) = 1800 frame (60 detik)
   Durasi per scene sengaja panjang supaya nyaman untuk dubbing.
   ══════════════════════════════════════════════════════════════════ */

export const FPS = 30;
export const SCENE = 300; // 10 detik per scene
export const TOTAL = SCENE * 6; // 1800 frame = 60 detik

/** Titik mulai tiap scene (frame global). */
export const T = {
  hook: 0, //  0s - 10s  masalah bos barbershop
  kasir: 300, // 10s - 20s  solusi Pangkasin + kasir cepat + nota WA
  bonus: 600, // 20s - 30s  bonus barber + loyalty
  aman: 900, // 30s - 40s  anti-curang + laporan laba bersih
  ai: 1200, // 40s - 50s  AI WhatsApp omzet
  cta: 1500, // 50s - 60s  multi-device + promo + CTA
};

const INK = C.ink;
const bd = (w = 6) => `${w}px solid ${INK}`;
const sh = (n = 10) => `${n}px ${n}px 0 ${INK}`;

/** Latar cream + titik-titik khas neo-brutalism. */
const F: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({
  children,
  style,
}) => (
  <AbsoluteFill style={{ background: C.cream, fontFamily: FONT, color: INK, ...style }}>
    <AbsoluteFill
      style={{
        backgroundImage: `radial-gradient(circle, ${INK} 1.4px, transparent 1.4px)`,
        backgroundSize: "38px 38px",
        opacity: 0.07,
      }}
    />
    {children}
  </AbsoluteFill>
);

/** Kartu putih neo-brutalism. */
const Card: React.FC<{
  children: React.ReactNode;
  bg?: string;
  padding?: number;
  shadow?: number;
  style?: React.CSSProperties;
}> = ({ children, bg = C.white, padding = 34, shadow = 10, style }) => (
  <div
    style={{
      background: bg,
      border: bd(6),
      borderRadius: 14,
      boxShadow: sh(shadow),
      padding,
      boxSizing: "border-box",
      ...style,
    }}
  >
    {children}
  </div>
);

/** Chip label uppercase. */
const Chip: React.FC<{
  children: React.ReactNode;
  bg?: string;
  size?: number;
  style?: React.CSSProperties;
}> = ({ children, bg = C.yellow, size = 30, style }) => (
  <span
    style={{
      display: "inline-block",
      background: bg,
      border: bd(4),
      borderRadius: 10,
      boxShadow: sh(5),
      padding: "10px 24px",
      fontSize: size,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: 3,
      fontFamily: FONT,
      ...style,
    }}
  >
    {children}
  </span>
);

/** Animasi masuk berbasis spring() + interpolate(). */
const In: React.FC<{
  delay?: number;
  dir?: "up" | "down" | "left" | "right";
  zoom?: boolean;
  distance?: number;
  damping?: number;
  stiffness?: number;
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({
  delay = 0,
  dir = "up",
  zoom = false,
  distance = 90,
  damping = 15,
  stiffness = 110,
  style,
  children,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const s = spring({ frame: frame - delay, fps, config: { damping, stiffness } });
  const d = interpolate(s, [0, 1], [distance, 0]);
  const t =
    dir === "up"
      ? `translate(${0}px, ${d}px)`
      : dir === "down"
        ? `translate(0px, ${-d}px)`
        : dir === "left"
          ? `translate(${-d}px, 0px)`
          : `translate(${d}px, 0px)`;
  return (
    <div
      style={{
        transform: zoom ? `scale(${interpolate(s, [0, 1], [0.7, 1])})` : t,
        opacity: s,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

/** Pembungkus scene: fade masuk 8 frame, fade keluar 14 frame terakhir. */
const Scene: React.FC<{ children: React.ReactNode; bg?: string }> = ({ children, bg = C.cream }) => {
  const frame = useCurrentFrame();
  const opacity = interpolate(frame, [0, 8, SCENE - 16, SCENE], [0, 1, 1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={{ background: bg, opacity }}>
      <F>{children}</F>
    </AbsoluteFill>
  );
};

const IconBox: React.FC<{ glyph: string; bg: string; size?: number }> = ({
  glyph,
  bg,
  size = 78,
}) => (
  <div
    style={{
      width: size,
      height: size,
      flexShrink: 0,
      background: bg,
      border: bd(5),
      borderRadius: 12,
      boxShadow: sh(5),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: size * 0.5,
      lineHeight: 1,
    }}
  >
    {glyph}
  </div>
);

/* --- kursor + ripple klik ---------------------------------------- */
const Cursor: React.FC<{ x: number; y: number; scale?: number }> = ({ x, y, scale = 1 }) => (
  <div
    style={{
      position: "absolute",
      left: 0,
      top: 0,
      transform: `translate(${x}px, ${y}px) scale(${scale})`,
      transformOrigin: "top left",
      zIndex: 60,
      filter: "drop-shadow(4px 5px 0 rgba(26,26,46,0.45))",
    }}
  >
    <svg width="52" height="66" viewBox="0 0 40 50">
      <path
        d="M4 2 L4 38 L14 28 L21 46 L29 42.5 L22 25.5 L35 25.5 Z"
        fill={C.white}
        stroke={INK}
        strokeWidth="3.5"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const ClickRipple: React.FC<{ x: number; y: number; start?: number; color?: string }> = ({
  x,
  y,
  start = 0,
  color = C.ok,
}) => {
  const frame = useCurrentFrame();
  const p = interpolate(frame - start, [0, 22], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  if (p <= 0 || p >= 1) return null;
  const size = interpolate(p, [0, 1], [40, 260]);
  return (
    <div
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: size,
        height: size,
        marginLeft: -size / 2,
        marginTop: -size / 2,
        borderRadius: "50%",
        border: `8px solid ${color}`,
        opacity: interpolate(p, [0, 0.25, 1], [0, 0.9, 0]),
        zIndex: 55,
      }}
    />
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCENE 1 — 0-299 (0s-10s) : HOOK & MASALAH BOS BARBERSHOP
   ══════════════════════════════════════════════════════════════════ */
const PROBLEMS = [
  { tag: "Kas", t: "Uang di laci nggak cocok" },
  { tag: "Komisi", t: "Bingung hitung bonus barber" },
  { tag: "Loyalty", t: "Pelanggan setia nggak dapat reward" },
  { tag: "Laba", t: "Nggak tahu untung atau cuma ramai" },
];

const Scene1Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const s = spring({ frame, fps, config: { damping: 14, stiffness: 95 } });

  return (
    <Scene>
      <AbsoluteFill
        style={{
          padding: "64px 110px",
          boxSizing: "border-box",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <In delay={0} distance={50}>
            <Chip bg={C.red} style={{ color: C.white }}>
              Masalah klasik barbershop
            </Chip>
          </In>
          <In delay={10} distance={60} damping={13}>
            <div
              style={{
                fontSize: 84,
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: -3,
                marginTop: 26,
                textTransform: "uppercase",
              }}
            >
              Bos Barbershop, masih catat di buku?
            </div>
          </In>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 34,
            marginTop: 56,
          }}
        >
          {PROBLEMS.map((p, i) => (
            <In key={p.tag} delay={26 + i * 20} dir={i % 2 === 0 ? "left" : "right"} damping={14}>
              <Card
                padding={28}
                shadow={9}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 26,
                  background: C.white,
                }}
              >
                <IconBox glyph="❌" bg={C.red} size={80} />
                <div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      letterSpacing: 3,
                      textTransform: "uppercase",
                      color: C.red,
                    }}
                  >
                    {p.tag}
                  </div>
                  <div style={{ fontSize: 40, fontWeight: 800, lineHeight: 1.2, marginTop: 4 }}>
                    {p.t}
                  </div>
                </div>
              </Card>
            </In>
          ))}
        </div>

        <In delay={128} distance={40} style={{ textAlign: "center", marginTop: 46 }}>
          <Chip bg={C.yellow} size={34}>
            Kalau iya, ini buat Anda 👇
          </Chip>
        </In>
      </AbsoluteFill>
    </Scene>
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCENE 2 — 300-599 (10s-20s) : SOLUSI PANGKASIN + KASIR + NOTA WA
   ══════════════════════════════════════════════════════════════════ */
const BTN = { x: 620, y: 842 }; // titik tengah tombol "Kirim Nota WhatsApp" (diverifikasi via render)

const KASIR_ROW = [
  { name: "Potong Rambut", barber: "Bang Raka", price: 35000 },
  { name: "Potong + Keramas", barber: "Bang Raka", price: 50000 },
  { name: "Cukur Jenggot", barber: "Adi", price: 20000 },
];

const KasirRow: React.FC<{ name: string; barber: string; price: number; index: number }> = ({
  name,
  barber,
  price,
  index,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const at = 70 + index * 16;
  const s = spring({ frame: frame - at, fps, config: { damping: 15, stiffness: 120 } });
  const tick = interpolate(frame, [at + 16, at + 30], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 18,
        background: C.cream,
        border: bd(4),
        borderRadius: 10,
        padding: "12px 18px",
        marginBottom: 12,
        opacity: s,
        transform: `translateX(${interpolate(s, [0, 1], [-60, 0])}px)`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 8,
            background: C.lime,
            border: bd(4),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 26,
            transform: `scale(${tick})`,
          }}
        >
          ✓
        </div>
        <div>
          <div style={{ fontSize: 32, fontWeight: 800 }}>{name}</div>
          <div style={{ fontSize: 23, fontWeight: 700, color: C.ink600 }}>{barber}</div>
        </div>
      </div>
      <div style={{ fontSize: 32, fontWeight: 800 }}>{rp(price)}</div>
    </div>
  );
};

const Scene2Kasir: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const total = KASIR_ROW.reduce((acc, r) => acc + r.price, 0);

  // zoom-in mockup
  const zoom = spring({ frame: frame - 24, fps, config: { damping: 18, stiffness: 80 } });
  const mockScale = interpolate(zoom, [0, 1], [0.72, 1]);

  // kursor: jalan (frame 150) → klik (frame 214) → tombol jadi "Terkirim"
  const travel = spring({ frame: frame - 150, fps, config: { damping: 19, stiffness: 62 } });
  const cx = interpolate(travel, [0, 1], [1620, BTN.x]);
  const cy = interpolate(travel, [0, 1], [980, BTN.y]);
  const press = interpolate(frame, [212, 216, 224], [1, 0.86, 0.86], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const sent = frame >= 224;
  const payActive = (i: number) =>
    interpolate(frame, [86 + i * 10, 96 + i * 10], [0, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    });

  return (
    <Scene>
      <AbsoluteFill style={{ padding: "54px 110px", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 26 }}>
          <In delay={0} distance={50} dir="left">
            <Pole height={112} width={40} />
          </In>
          <In delay={4} distance={50} damping={14}>
            <div>
              <div
                style={{
                  fontSize: 78,
                  fontWeight: 800,
                  lineHeight: 1.05,
                  letterSpacing: -2.5,
                  textTransform: "uppercase",
                }}
              >
                Pangkasin — Kasir Barbershop 💈
              </div>
              <div style={{ fontSize: 30, fontWeight: 700, color: C.ink600, marginTop: 8 }}>
                Kasir, barber, nota, dan laporan — satu aplikasi.
              </div>
            </div>
          </In>
        </div>

        <div style={{ display: "flex", gap: 34, marginTop: 34 }}>
          {/* mockup kasir */}
          <div
            style={{
              width: 1000,
              height: 640,
              transform: `scale(${mockScale})`,
              transformOrigin: "top left",
              opacity: interpolate(zoom, [0, 1], [0, 1]),
              background: C.white,
              border: bd(6),
              borderRadius: 16,
              boxShadow: sh(12),
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "14px 20px",
                background: C.ink,
                color: C.white,
                fontSize: 24,
                fontWeight: 800,
              }}
            >
              <span>Pangkasin · Kasir — Meja 2 · Rizky</span>
              <span style={{ marginLeft: "auto" }}>● Online</span>
            </div>

            <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", flex: 1 }}>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  letterSpacing: 3,
                  textTransform: "uppercase",
                  color: C.ink600,
                  marginBottom: 10,
                }}
              >
                Layanan dipilih
              </div>

              {KASIR_ROW.map((r, i) => (
                <KasirRow key={r.name} {...r} index={i} />
              ))}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: 4,
                  paddingTop: 12,
                  borderTop: `4px dashed ${INK}`,
                }}
              >
                <div style={{ display: "flex", gap: 12 }}>
                  {["Cash", "QRIS", "Transfer"].map((m, i) => (
                    <div
                      key={m}
                      style={{
                        padding: "8px 20px",
                        borderRadius: 10,
                        fontSize: 24,
                        fontWeight: 800,
                        border: bd(4),
                        background: i === 1 ? C.yellow : C.white,
                        transform: `scale(${interpolate(payActive(i), [0, 1], [0.7, 1])})`,
                        opacity: payActive(i),
                      }}
                    >
                      {m}
                    </div>
                  ))}
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: C.ink600 }}>TOTAL</div>
                  <div style={{ fontSize: 46, fontWeight: 800, letterSpacing: -1.5 }}>
                    <Counter to={total} delay={88} durationInFrames={34} format={rp} />
                  </div>
                </div>
              </div>

              {/* tombol kirim nota — target klik kursor */}
              <div
                style={{
                  marginTop: "auto",
                  height: 84,
                  border: bd(6),
                  borderRadius: 12,
                  boxShadow: sh(8),
                  background: sent ? C.ok : C.yellow,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 14,
                  fontSize: 36,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: 2,
                  transform: `scale(${press})`,
                }}
              >
                📲 {sent ? "Nota terkirim ✓" : "Kirim Nota WhatsApp"}
              </div>
            </div>
          </div>

          {/* kolom fitur */}
          <div style={{ display: "flex", flexDirection: "column", gap: 26, width: 640 }}>
            {[
              {
                t: "Kasir cepat",
                d: "Pilih layanan, pilih barber, bayar Cash, QRIS, atau Transfer.",
                bg: C.blue,
              },
              {
                t: "Nota digital ke WhatsApp",
                d: "Pakai logo & nama barbershop Anda.",
                bg: C.lime,
              },
            ].map((f, i) => (
              <In key={f.t} delay={40 + i * 22} dir="right" damping={14}>
                <Card shadow={10} style={{ display: "flex", gap: 24, alignItems: "center" }}>
                  <IconBox glyph="✅" bg={f.bg} size={78} />
                  <div>
                    <div style={{ fontSize: 42, fontWeight: 800 }}>{f.t}</div>
                    <div
                      style={{
                        fontSize: 28,
                        fontWeight: 700,
                        color: C.ink600,
                        lineHeight: 1.35,
                        marginTop: 6,
                      }}
                    >
                      {f.d}
                    </div>
                  </div>
                </Card>
              </In>
            ))}

            <In delay={120} dir="right">
              <div
                style={{
                  border: bd(5),
                  borderRadius: 12,
                  boxShadow: sh(7),
                  background: sent ? C.lime : C.white,
                  padding: "18px 24px",
                  fontSize: 30,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                {sent ? "✓ Nota terkirim ke WhatsApp Andi" : "Menunggu nota dikirim…"}
              </div>
            </In>
          </div>
        </div>
      </AbsoluteFill>

      <Cursor x={cx} y={cy} scale={press} />
      <ClickRipple x={BTN.x} y={BTN.y} start={214} color={C.ok} />
    </Scene>
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCENE 3 — 600-899 (20s-30s) : BONUS BARBER OTOMATIS + LOYALTY
   ══════════════════════════════════════════════════════════════════ */
const KOMISI = [
  { layanan: "Potong Rambut", txn: 62, komisi: 620000 },
  { layanan: "Potong + Keramas", txn: 41, komisi: 615000 },
  { layanan: "Cukur Jenggot", txn: 28, komisi: 280000 },
  { layanan: "Creambath", txn: 12, komisi: 360000 },
];

const KomisiRow: React.FC<{ layanan: string; txn: number; komisi: number; index: number }> = ({
  layanan,
  txn,
  komisi,
  index,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const at = 30 + index * 18;
  const s = spring({ frame: frame - at, fps, config: { damping: 15, stiffness: 120 } });
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 0",
        borderBottom: `3px solid ${C.ink100}`,
        opacity: s,
        transform: `translateX(${interpolate(s, [0, 1], [-50, 0])}px)`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <span style={{ fontSize: 26, fontWeight: 800, color: C.ok }}>✓</span>
        <span style={{ fontSize: 30, fontWeight: 800 }}>{layanan}</span>
        <span style={{ fontSize: 23, fontWeight: 700, color: C.ink600 }}>{txn}×</span>
      </div>
      <div style={{ fontSize: 31, fontWeight: 800 }}>{rp(komisi)}</div>
    </div>
  );
};

const Stamp: React.FC<{ index: number }> = ({ index }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const at = 60 + index * 24;
  const s = spring({ frame: frame - at, fps, config: { damping: 10, stiffness: 150 } });
  const filled = frame - at >= 0;
  return (
    <div
      style={{
        width: 104,
        height: 104,
        borderRadius: 12,
        border: bd(5),
        background: filled ? C.yellow : C.cream,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 48,
        transform: `scale(${interpolate(s, [0, 1], [0.3, 1])})`,
        opacity: filled ? 1 : 0.5,
        boxShadow: filled ? sh(6) : "none",
      }}
    >
      {filled ? "💈" : ""}
    </div>
  );
};

const Scene3Bonus: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const granted = frame >= 276; // stempel ke-10 terisi di frame 60 + 9*24 = 276
  const gift = spring({ frame: frame - 276, fps, config: { damping: 9, stiffness: 130 } });

  return (
    <Scene>
      <AbsoluteFill style={{ padding: "54px 110px", boxSizing: "border-box" }}>
        <div style={{ textAlign: "center" }}>
          <In delay={0} distance={40}>
            <Chip bg={C.lime} size={32}>
              Dihitung otomatis
            </Chip>
          </In>
          <In delay={10} distance={50} damping={14}>
            <div
              style={{
                fontSize: 68,
                fontWeight: 800,
                letterSpacing: -2.5,
                marginTop: 20,
                textTransform: "uppercase",
              }}
            >
              Bonus barber & loyalty jalan sendiri
            </div>
          </In>
        </div>

        <div style={{ display: "flex", gap: 34, marginTop: 34, alignItems: "stretch" }}>
          {/* kartu kiri */}
          <In delay={18} dir="left" style={{ flex: 1 }}>
            <Card shadow={10} style={{ height: 620 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                <IconBox glyph="💰" bg={C.yellow} size={78} />
                <div style={{ fontSize: 46, fontWeight: 800 }}>Bonus barber otomatis</div>
              </div>

              <div style={{ marginTop: 18 }}>
                {KOMISI.map((k, i) => (
                  <KomisiRow key={k.layanan} {...k} index={i} />
                ))}
              </div>

              <div
                style={{
                  marginTop: 24,
                  border: bd(5),
                  borderRadius: 12,
                  boxShadow: sh(7),
                  background: C.blue,
                  padding: "20px 24px",
                }}
              >
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 800,
                    letterSpacing: 3,
                    textTransform: "uppercase",
                  }}
                >
                  Total gaji + bonus bulan ini
                </div>
                <div style={{ fontSize: 76, fontWeight: 800, lineHeight: 1.05, letterSpacing: -3 }}>
                  <Counter to={3875000} delay={60} durationInFrames={60} format={rp} />
                </div>
                <div style={{ fontSize: 25, fontWeight: 700, marginTop: 6 }}>
                  Gaji + komisi + bonus target tiap bulan
                </div>
              </div>
            </Card>
          </In>

          {/* kartu kanan */}
          <In delay={26} dir="right" style={{ width: 700 }}>
            <Card shadow={10} bg={C.white} style={{ height: 620 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                <IconBox glyph="🎟️" bg={C.pink} size={78} />
                <div>
                  <div style={{ fontSize: 46, fontWeight: 800 }}>Loyalty</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: C.ink600 }}>
                    Potong 10×, Gratis 1×
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(5, 1fr)",
                  gap: 16,
                  justifyItems: "center",
                  marginTop: 30,
                }}
              >
                {Array.from({ length: 10 }).map((_, i) => (
                  <Stamp key={i} index={i} />
                ))}
              </div>

              <div
                style={{
                  marginTop: 30,
                  border: bd(5),
                  borderRadius: 12,
                  boxShadow: sh(7),
                  background: granted ? C.lime : C.cream,
                  padding: "20px 24px",
                  transform: `scale(${granted ? interpolate(gift, [0, 1], [0.9, 1]) : 1})`,
                }}
              >
                <div style={{ fontSize: 40, fontWeight: 800, textTransform: "uppercase" }}>
                  {granted ? "🎉 Gratis 1× potong!" : "Menghitung stempel…"}
                </div>
                <div style={{ fontSize: 26, fontWeight: 700, color: C.ink600, marginTop: 6 }}>
                  Stempel digital, sistem yang menghitung
                </div>
              </div>
            </Card>
          </In>
        </div>

        <In delay={80} style={{ textAlign: "center", marginTop: 30 }}>
          <div style={{ fontSize: 32, fontWeight: 800 }}>
            ✅ Barber transparan komisinya, pelanggan balik lagi
          </div>
        </In>
      </AbsoluteFill>
    </Scene>
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCENE 4 — 900-1199 (30s-40s) : ANTI-CURANG & LABA BERSIH
   ══════════════════════════════════════════════════════════════════ */
const HARI = ["S", "S", "R", "K", "J", "S", "M"];
const BAR = [0.52, 0.61, 0.44, 0.7, 0.58, 0.95, 0.78];

const Scene4Aman: React.FC = () => {
  const frame = useCurrentFrame();
  const metrics = [
    { k: "Jam ramai", v: "19.00 – 21.00" },
    { k: "Layanan terlaris", v: "Potong + Keramas" },
    { k: "Barber terlaris", v: "Bang Raka" },
  ];

  return (
    <Scene>
      <AbsoluteFill
        style={{
          padding: "46px 110px",
          boxSizing: "border-box",
          justifyContent: "center",
        }}
      >
        {/* banner keamanan */}
        <In delay={0} dir="down" distance={70}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 26,
              border: bd(6),
              borderRadius: 14,
              boxShadow: sh(10),
              background: C.yellow,
              padding: "22px 32px",
            }}
          >
            <IconBox glyph="🔒" bg={C.white} size={80} />
            <div style={{ fontSize: 48, fontWeight: 800, lineHeight: 1.2, letterSpacing: -1.5 }}>
              Transaksi nggak bisa diedit atau dihapus karyawan 🔒
            </div>
          </div>
        </In>

        <div style={{ display: "flex", gap: 34, marginTop: 30, alignItems: "stretch" }}>
          {/* dashboard omzet */}
          <In delay={20} dir="left" style={{ width: 1040 }}>
            <Card shadow={10} style={{ height: 560 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                <IconBox glyph="📊" bg={C.blue} size={72} />
                <div style={{ fontSize: 42, fontWeight: 800 }}>
                  ✅ Dashboard omzet — grafik harian
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 24,
                  height: 250,
                  marginTop: 24,
                }}
              >
                {BAR.map((b, i) => {
                  const h = interpolate(frame, [50 + i * 12, 96 + i * 12], [0, 1], {
                    extrapolateLeft: "clamp",
                    extrapolateRight: "clamp",
                  });
                  return (
                    <div
                      key={i}
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: 205 * b * h,
                          background: i === 5 ? C.yellow : C.blue,
                          border: bd(4),
                          borderRadius: 8,
                          boxShadow: sh(5),
                        }}
                      />
                      <div style={{ fontSize: 24, fontWeight: 800 }}>{HARI[i]}</div>
                    </div>
                  );
                })}
              </div>

              <div style={{ display: "flex", gap: 16, marginTop: 20 }}>
                {metrics.map((m, i) => (
                  <In key={m.k} delay={100 + i * 16} distance={40} style={{ flex: 1 }}>
                    <div
                      style={{
                        border: bd(4),
                        borderRadius: 10,
                        background: C.cream,
                        padding: "12px 18px",
                      }}
                    >
                      <div style={{ fontSize: 20, fontWeight: 800, color: C.ink600 }}>{m.k}</div>
                      <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4 }}>{m.v}</div>
                    </div>
                  </In>
                ))}
              </div>
            </Card>
          </In>

          {/* laporan laba bersih */}
          <In delay={30} dir="right" style={{ flex: 1 }}>
            <Card shadow={10} style={{ height: 560, display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                <IconBox glyph="📈" bg={C.lime} size={72} />
                <div style={{ fontSize: 38, fontWeight: 800 }}>✅ Laporan laba bersih</div>
              </div>

              <div style={{ marginTop: 22 }}>
                {[
                  { k: "Pemasukan bulan ini", v: 38600000, c: C.ok, sign: "+" },
                  { k: "Pengeluaran", v: 9750000, c: C.red, sign: "−" },
                ].map((r, i) => (
                  <div
                    key={r.k}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "14px 0",
                      borderBottom: `3px solid ${C.ink100}`,
                    }}
                  >
                    <div style={{ fontSize: 28, fontWeight: 800, color: C.ink600 }}>{r.k}</div>
                    <div style={{ fontSize: 32, fontWeight: 800 }}>
                      {r.sign}{" "}
                      <Counter to={r.v} delay={40 + i * 16} durationInFrames={44} format={rp} />
                    </div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  marginTop: 24,
                  border: bd(5),
                  borderRadius: 12,
                  boxShadow: sh(7),
                  background: C.ok,
                  padding: "18px 24px",
                }}
              >
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 800,
                    letterSpacing: 3,
                    textTransform: "uppercase",
                  }}
                >
                  Laba bersih
                </div>
                <div style={{ fontSize: 68, fontWeight: 800, lineHeight: 1.05, letterSpacing: -3 }}>
                  <Counter to={28850000} delay={70} durationInFrames={50} format={rp} />
                </div>
              </div>

              <In delay={130} dir="up" style={{ marginTop: "auto" }}>
                <div
                  style={{
                    border: bd(6),
                    borderRadius: 12,
                    boxShadow: sh(8),
                    background: C.pink,
                    padding: "18px 0",
                    textAlign: "center",
                    fontSize: 36,
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: 2,
                  }}
                >
                  📄 Export ke Excel
                </div>
              </In>
            </Card>
          </In>
        </div>
      </AbsoluteFill>
    </Scene>
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCENE 5 — 1200-1499 (40s-50s) : AI WHATSAPP OMZET
   ══════════════════════════════════════════════════════════════════ */
const TypingDots: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", height: 30 }}>
      {[0, 1, 2].map((i) => {
        const t = (frame - i * 6) % 22;
        const y = interpolate(t, [0, 11, 21], [0, -9, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });
        return (
          <div
            key={i}
            style={{
              width: 14,
              height: 14,
              borderRadius: 99,
              background: INK,
              transform: `translateY(${y}px)`,
            }}
          />
        );
      })}
    </div>
  );
};

const Scene5Ai: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const ask = spring({ frame: frame - 40, fps, config: { damping: 13, stiffness: 130 } });
  const typing = frame >= 70 && frame < 115;
  const reply = spring({ frame: frame - 115, fps, config: { damping: 13, stiffness: 120 } });

  return (
    <Scene>
      <AbsoluteFill
        style={{
          padding: "50px 110px",
          boxSizing: "border-box",
          justifyContent: "center",
        }}
      >
        <div style={{ display: "flex", gap: 46, alignItems: "center" }}>
          {/* mockup WhatsApp */}
          <div style={{ width: 760, flexShrink: 0 }}>
            <div
              style={{
                border: bd(6),
                borderRadius: 26,
                boxShadow: sh(12),
                background: "#e5ddd5",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  padding: "14px 20px",
                  background: C.ink,
                  color: C.white,
                }}
              >
                <div
                  style={{
                    width: 58,
                    height: 58,
                    borderRadius: 99,
                    background: C.lime,
                    border: bd(4),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 30,
                  }}
                >
                  🤖
                </div>
                <div>
                  <div style={{ fontSize: 32, fontWeight: 800 }}>Pangkasin AI</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: C.lime }}>online</div>
                </div>
              </div>

              <div
                style={{
                  padding: 24,
                  minHeight: 430,
                  display: "flex",
                  flexDirection: "column",
                  gap: 18,
                }}
              >
                {/* pertanyaan owner */}
                <div
                  style={{
                    alignSelf: "flex-end",
                    maxWidth: 520,
                    transform: `translateY(${interpolate(ask, [0, 1], [50, 0])}px) scale(${interpolate(
                      ask,
                      [0, 1],
                      [0.85, 1],
                    )})`,
                    opacity: ask,
                    background: C.white,
                    border: bd(4),
                    borderRadius: "18px 18px 6px 18px",
                    boxShadow: sh(5),
                    padding: "16px 22px",
                  }}
                >
                  <div style={{ fontSize: 32, fontWeight: 800 }}>omzet hari ini berapa?</div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: C.ink600,
                      textAlign: "right",
                      marginTop: 6,
                    }}
                  >
                    19:04 ✓✓
                  </div>
                </div>

                {typing ? (
                  <div
                    style={{
                      alignSelf: "flex-start",
                      background: C.white,
                      border: bd(4),
                      borderRadius: "18px 18px 18px 6px",
                      boxShadow: sh(5),
                      padding: "16px 22px",
                    }}
                  >
                    <TypingDots />
                  </div>
                ) : null}

                {/* balasan AI */}
                <div
                  style={{
                    alignSelf: "flex-start",
                    maxWidth: 660,
                    transform: `translateY(${interpolate(reply, [0, 1], [50, 0])}px) scale(${interpolate(
                      reply,
                      [0, 1],
                      [0.85, 1],
                    )})`,
                    opacity: reply,
                    background: C.lime,
                    border: bd(4),
                    borderRadius: "18px 18px 18px 6px",
                    boxShadow: sh(6),
                    padding: "20px 24px",
                  }}
                >
                  <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.3 }}>
                    🤖 Total Omzet Hari Ini: Rp 1.450.000 (29 Pelanggan). Barber Terlaris: Bang Raka.
                    Laba Bersih: Rp 1.280.000.
                  </div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: C.ink600,
                      textAlign: "right",
                      marginTop: 8,
                    }}
                  >
                    19:04 ✓✓
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* penjelasan */}
          <div style={{ flex: 1 }}>
            <In delay={0} distance={50} damping={14}>
              <div
                style={{
                  fontSize: 70,
                  fontWeight: 800,
                  lineHeight: 1.1,
                  letterSpacing: -2.5,
                  textTransform: "uppercase",
                }}
              >
                Tanya omzet, AI yang jawab
              </div>
            </In>

            <In delay={100} dir="right">
              <Card
                shadow={10}
                bg={C.lime}
                style={{ marginTop: 30, display: "flex", gap: 22, alignItems: "center" }}
              >
                <IconBox glyph="✅" bg={C.white} size={70} />
                <div style={{ fontSize: 38, fontWeight: 800, lineHeight: 1.3 }}>
                  Tanya omzet lewat WhatsApp, AI jawab dari data Anda!
                </div>
              </Card>
            </In>

            <div style={{ marginTop: 26, display: "grid", gap: 16 }}>
              {[
                "Omzet, laba, jumlah pelanggan",
                "Barber & layanan terlaris",
                "Stok tipis dan pengeluaran",
              ].map((t, i) => (
                <In key={t} delay={130 + i * 20} dir="right">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      border: bd(4),
                      borderRadius: 10,
                      background: C.white,
                      boxShadow: sh(5),
                      padding: "14px 20px",
                      fontSize: 30,
                      fontWeight: 800,
                    }}
                  >
                    <span style={{ color: C.ok }}>✓</span> {t}
                  </div>
                </In>
              ))}
            </div>
          </div>
        </div>
      </AbsoluteFill>
    </Scene>
  );
};

/* ══════════════════════════════════════════════════════════════════
   SCENE 6 — 1500-1799 (50s-60s) : MULTI-DEVICE, PROMO, CTA
   ══════════════════════════════════════════════════════════════════ */
const Scene6Cta: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const badge = spring({ frame, fps, config: { damping: 14, stiffness: 100 } });
  const head = spring({ frame: frame - 20, fps, config: { damping: 12, stiffness: 95 } });
  const cta = spring({ frame: frame - 60, fps, config: { damping: 10, stiffness: 110 } });
  const pulse = 1 + Math.sin(frame / 9) * 0.022;
  const ctaPulse = 1 + Math.sin(frame / 12) * 0.018;

  return (
    <Scene>
      <AbsoluteFill
        style={{
          padding: "70px 110px",
          boxSizing: "border-box",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 16,
            border: bd(5),
            borderRadius: 999,
            boxShadow: sh(7),
            background: C.blue,
            padding: "14px 34px",
            fontSize: 34,
            fontWeight: 800,
            transform: `translateY(${interpolate(badge, [0, 1], [-50, 0])}px) scale(${interpolate(
              badge,
              [0, 1],
              [0.85, 1],
            )})`,
            opacity: badge,
          }}
        >
          📱 Bisa dibuka dari HP atau laptop, tanpa install.
        </div>

        <div
          style={{
            marginTop: 44,
            fontSize: 96,
            fontWeight: 800,
            lineHeight: 1.08,
            letterSpacing: -4,
            textTransform: "uppercase",
            transform: `scale(${pulse})`,
            opacity: head,
          }}
        >
          🎁 Gratis 2 bulan
          <div style={{ fontSize: 54, letterSpacing: -2, marginTop: 14 }}>
            Coba dulu, rasakan bedanya.
          </div>
        </div>

        <div
          style={{
            marginTop: 46,
            transform: `scale(${ctaPulse})`,
            opacity: cta,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 18,
              border: bd(7),
              borderRadius: 18,
              boxShadow: sh(12),
              background: C.yellow,
              padding: "28px 56px",
              fontSize: 62,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            📲 Daftar: 0895397073311
          </div>
        </div>

        <div
          style={{
            marginTop: 34,
            display: "inline-block",
            border: bd(5),
            borderRadius: 12,
            boxShadow: sh(7),
            background: C.white,
            padding: "14px 40px",
            fontSize: 46,
            fontWeight: 800,
            letterSpacing: 1,
            opacity: interpolate(frame, [80, 100], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            }),
          }}
        >
          {INFO.site}
        </div>

        <div
          style={{
            marginTop: 20,
            fontSize: 28,
            fontWeight: 800,
            color: C.ink600,
            opacity: interpolate(frame, [95, 115], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            }),
          }}
        >
          Pangkasin — aplikasi kasir & manajemen barbershop 💈
        </div>
      </AbsoluteFill>
    </Scene>
  );
};

/* ══════════════════════════════════════════════════════════════════
   KOMPOSISI — 6 sequence × 300 frame = 1800 frame (60 detik)
   ══════════════════════════════════════════════════════════════════ */
export const PangkasinNeoBrutal: React.FC = () => {
  useOnestFonts();
  return (
    <AbsoluteFill style={{ background: C.cream }}>
      <Sequence from={T.hook} durationInFrames={SCENE} name="1 · Hook & masalah">
        <Scene1Hook />
      </Sequence>
      <Sequence from={T.kasir} durationInFrames={SCENE} name="2 · Kasir cepat + nota WA">
        <Scene2Kasir />
      </Sequence>
      <Sequence from={T.bonus} durationInFrames={SCENE} name="3 · Bonus barber + loyalty">
        <Scene3Bonus />
      </Sequence>
      <Sequence from={T.aman} durationInFrames={SCENE} name="4 · Anti-curang + laba bersih">
        <Scene4Aman />
      </Sequence>
      <Sequence from={T.ai} durationInFrames={SCENE} name="5 · AI WhatsApp omzet">
        <Scene5Ai />
      </Sequence>
      <Sequence from={T.cta} durationInFrames={SCENE} name="6 · Multi-device + CTA">
        <Scene6Cta />
      </Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
